import Constants from 'expo-constants';
import { Formik, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native';

import { CheckboxInputFormik } from '@fhs/auth';
import { Text, tokens } from '@fhs/ui';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import {
  CREDIT_CARD_FORM_CVV_INPUT,
  CREDIT_CARD_FORM_EXPIRY_INPUT,
  CREDIT_CARD_FORM_NAME_INPUT,
  CREDIT_CARD_FORM_NUMBER_INPUT,
  CREDIT_CARD_FORM_POSTAL_CODE_INPUT,
  CREDIT_CARD_FORM_SAVE_CARD_CHECKBOX,
  NEW_CARD_FORM_CONTAINER,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { type CardData, usePaymentFlowStore, usePaymentMethodStore } from '../..';
import { EncryptionWebView } from '../encryption-webview';

import FormFieldTitle from './form-field-title';
import { validationSchema } from './new-card-validation-schema';

interface NewCardFormData extends CardData {
  expirationDate: string;
}

function FormikToStoreSync() {
  const { values, isValid } = useFormikContext<NewCardFormData>();
  const setNewCardData = usePaymentMethodStore(s => s.setNewCardData);
  const setCreditType = usePaymentMethodStore(s => s.setCreditType);

  useEffect(() => {
    const hasAllRequired =
      !!values.fullName &&
      !!values.cardNumber &&
      !!values.cvv &&
      !!values.expiryMonth &&
      !!values.expiryYear &&
      !!values.postalCode;

    if (hasAllRequired && isValid) {
      // Card number and CVV must be digits-only for Worldpay/Orbital (avoids "Account number not numeric")
      const digitsOnly = (s: string) => (s ?? '').replace(/\D/g, '');
      setNewCardData({
        fullName: values.fullName,
        cardNumber: digitsOnly(values.cardNumber),
        cvv: digitsOnly(values.cvv),
        expiryMonth: values.expiryMonth,
        expiryYear: values.expiryYear,
        postalCode: values.postalCode,
        vaultCard: values.vaultCard,
      });
      setCreditType(values.cardNumber);
    } else {
      // Clear the store whenever we don't have all required fields or form is invalid to disable continue button
      setNewCardData(null);
    }
    // remove on unmount
    return () => setNewCardData(null);
  }, [values, isValid, setNewCardData, setCreditType]);

  return null;
}

export function NewCardForm() {
  const newCardData = usePaymentMethodStore(state => state.newCardData);
  const currentState = usePaymentFlowStore(state => state.currentState);

  const zipOrPostalLabel =
    Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? 'Zip Code' : 'Postal Code';

  const isProcessing = currentState === 'encrypting';
  const autoFill = useFlag('autofill-test-card');

  const initialValues: NewCardFormData = {
    fullName: newCardData?.fullName || '',
    cardNumber: newCardData?.cardNumber || '',
    cvv: newCardData?.cvv || '',
    expiryMonth: newCardData?.expiryMonth || '',
    expiryYear: newCardData?.expiryYear || '',
    postalCode: newCardData?.postalCode || '',
    vaultCard: newCardData?.vaultCard ?? true,
    expirationDate: '',
  };

  const prefilledValues = {
    fullName: 'John Smith',
    cardNumber: '4445222299990007'.replace(/ +/g, ''),
    cvv: '382',
    expiryMonth: '12',
    expiryYear: '26',
    postalCode: '33606',
    vaultCard: true,
    expirationDate: '12/26',
  };

  return (
    <Formik<NewCardFormData>
      initialValues={autoFill ? prefilledValues : initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
      validateOnBlur={true}
      validateOnChange={false}
    >
      {({ handleBlur, handleChange, setFieldValue, values, errors, touched }) => (
        <>
          <FormikToStoreSync />
          <View testID={NEW_CARD_FORM_CONTAINER} style={styles.form}>
            {/* Full Name */}
            <View style={styles.inputGroup}>
              <FormFieldTitle title="Cardholder Name" required={true} />

              <TextInput
                testID={CREDIT_CARD_FORM_NAME_INPUT}
                style={[styles.input, touched.fullName && errors.fullName && styles.inputError]}
                value={values.fullName}
                onChangeText={handleChange('fullName')}
                onBlur={handleBlur('fullName')}
                editable={!isProcessing}
                placeholder="Full name as it appears on card"
                placeholderTextColor={tokens.colors.$blackOpacity30}
              />
              {touched.fullName && errors.fullName && (
                <Text style={styles.errorText}>{errors.fullName}</Text>
              )}
            </View>

            {/* Card Number */}
            <View style={styles.inputGroup}>
              <FormFieldTitle title="Credit Card Number" required={true} />
              <TextInput
                testID={CREDIT_CARD_FORM_NUMBER_INPUT}
                style={[styles.input, touched.cardNumber && errors.cardNumber && styles.inputError]}
                value={formatCardForDisplay(values.cardNumber)}
                onChangeText={handleChange('cardNumber')}
                onBlur={handleBlur('cardNumber')}
                keyboardType="number-pad"
                maxLength={19}
                editable={!isProcessing}
                placeholder="____ ____ ____ ____"
                placeholderTextColor={tokens.colors.$blackOpacity30}
                // secureTextEntry
              />
              {touched.cardNumber && errors.cardNumber && (
                <Text style={styles.errorText}>{errors.cardNumber}</Text>
              )}
            </View>

            {/* Expiry and CVV */}
            <View style={styles.row}>
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <FormFieldTitle title="Expiration Date" required={true} />
                <TextInput
                  testID={CREDIT_CARD_FORM_EXPIRY_INPUT}
                  style={[
                    styles.input,
                    touched.expirationDate && errors.expirationDate && styles.inputError,
                  ]}
                  value={values.expirationDate}
                  onChangeText={text => {
                    // Auto-format to MM/YY
                    let formatted = text.replace(/\D/g, '');
                    if (formatted.length >= 3) {
                      formatted = `${formatted.slice(0, 2)}/${formatted.slice(2, 4)}`;
                    }

                    handleChange('expirationDate')(formatted);

                    // Split into separate fields
                    const [month, year] = formatted.split('/');
                    setFieldValue('expiryMonth', month || '');
                    setFieldValue('expiryYear', year || '');
                  }}
                  onBlur={handleBlur('expirationDate')}
                  keyboardType="number-pad"
                  maxLength={5}
                  placeholder="MM/YY"
                  placeholderTextColor={tokens.colors.$blackOpacity30}
                  editable={!isProcessing}
                />
                {touched.expirationDate && errors.expirationDate && (
                  <Text style={styles.errorText}>{errors.expirationDate}</Text>
                )}
              </View>

              <View style={[styles.inputGroup, styles.halfWidth]}>
                <FormFieldTitle title="CVV" required={true} />

                <TextInput
                  testID={CREDIT_CARD_FORM_CVV_INPUT}
                  style={[styles.input, touched.cvv && errors.cvv && styles.inputError]}
                  value={values.cvv}
                  onChangeText={handleChange('cvv')}
                  onBlur={handleBlur('cvv')}
                  placeholder="Enter the CVV"
                  placeholderTextColor={tokens.colors.$blackOpacity30}
                  keyboardType="number-pad"
                  maxLength={4}
                  secureTextEntry
                  editable={!isProcessing}
                />
                {touched.cvv && errors.cvv && <Text style={styles.errorText}>{errors.cvv}</Text>}
              </View>
            </View>

            {/* Zip/Postal Code */}
            <View style={styles.inputGroup}>
              <FormFieldTitle title={zipOrPostalLabel} required={true} />

              <TextInput
                testID={CREDIT_CARD_FORM_POSTAL_CODE_INPUT}
                style={[styles.input, touched.postalCode && errors.postalCode && styles.inputError]}
                value={values.postalCode}
                onChangeText={handleChange('postalCode')}
                onBlur={handleBlur('postalCode')}
                maxLength={19}
                editable={!isProcessing}
                placeholder={`Enter the billing ${zipOrPostalLabel.toLowerCase()}`}
                placeholderTextColor={tokens.colors.$blackOpacity30}
              />
              {touched.postalCode && errors.postalCode && (
                <Text style={styles.errorText}>{errors.postalCode}</Text>
              )}
            </View>

            <CheckboxInputFormik
              name="vaultCard"
              component={() => (
                <View testID={CREDIT_CARD_FORM_SAVE_CARD_CHECKBOX}>
                  <Text.Ui size="lg">Save this card for future orders</Text.Ui>
                </View>
              )}
              label="save-card-checkbox"
            />
          </View>

          {Platform.OS !== 'web' && <EncryptionWebView cardData={values} />}
        </>
      )}
    </Formik>
  );
}
const styles = StyleSheet.create({
  form: {
    marginHorizontal: 30,
    marginVertical: 20,
  },
  inputError: {
    borderColor: tokens.colors.$error,
  },
  errorText: {
    color: tokens.colors.$error,
    fontSize: 12,
    marginTop: 4,
  },
  inputGroup: {
    marginBottom: 16,
  },
  input: {
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity30,
    borderRadius: 4,
    padding: 12,
    fontSize: 16,
    fontFamily: 'Montserrat',
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  halfWidth: {
    flexBasis: '48%',
    flexGrow: 1,
    flexShrink: 0,
  },
});

// Grouping by brand (basic heuristic: Amex = 34/37)
function formatCardForDisplay(digits: string) {
  const d = (digits || '').replace(/\D/g, '');

  const isAmex = /^3[47]\d{0,13}$/.test(d);
  if (isAmex) {
    // 15-digit Amex: 4-6-5 (works progressively while typing)
    const p1 = d.slice(0, 4);
    const p2 = d.slice(4, 10);
    const p3 = d.slice(10, 15);
    return [p1, p2, p3].filter(Boolean).join(' ');
  }

  // Default: groups of 4 (up to 19 digits)
  return d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}
